import type { ImportBody, MacroFactorImport } from '@gymcloud/macrofactor'

/*
 * What the import panel does with a reading once it has one: cuts it into the requests that
 * carry it, and counts what came in for the last step to say.
 */

/** How many entries go to the server in one request; the rest of the pieces fit in the first. */
const ENTRIES_PER_PAGE = 1500
const RECORDS_PER_PAGE = 4000

function pages<T>(records: readonly T[], size: number): T[][] {
  const out: T[][] = []
  for (let at = 0; at < records.length; at += size) {
    out.push(records.slice(at, at + size))
  }
  return out
}

/**
 * The reading, as the requests that carry it: the smaller pieces in the first, the entries
 * paged behind them. Every page is written the same way on the server, so one that arrives
 * twice — a retry — is written once.
 */
export function pagesOf(source: string, reading: MacroFactorImport): ImportBody[] {
  const out: ImportBody[] = []
  const foods = pages(reading.foods, RECORDS_PER_PAGE)
  const goals = pages(reading.goals, RECORDS_PER_PAGE)
  const weighIns = pages(reading.weighIns, RECORDS_PER_PAGE)
  const metrics = pages(reading.metrics, RECORDS_PER_PAGE)
  const longest = Math.max(foods.length, goals.length, weighIns.length, metrics.length, 1)
  for (let at = 0; at < longest; at += 1) {
    out.push({
      source,
      ...(at === 0 ? { profile: reading.profile } : {}),
      ...(foods[at] === undefined ? {} : { foods: foods[at] }),
      ...(goals[at] === undefined ? {} : { goals: goals[at] }),
      ...(weighIns[at] === undefined ? {} : { weighIns: weighIns[at] }),
      ...(metrics[at] === undefined ? {} : { metrics: metrics[at] }),
    })
  }
  for (const page of pages(reading.entries, ENTRIES_PER_PAGE)) {
    out.push({ source, entries: page })
  }
  return out
}

/** What came in, counted off the reading. */
export function summaryOf(reading: MacroFactorImport) {
  const days = new Set(reading.entries.map((entry) => entry.day))
  return {
    entries: reading.entries.length,
    days: days.size,
    from: reading.entries[0]?.day ?? null,
    to: reading.entries[reading.entries.length - 1]?.day ?? null,
    foods: reading.foods.length,
    goals: reading.goals.length,
    weighIns: reading.weighIns.length,
    stepDays: reading.metrics.length,
    inProgress: reading.goals.some((goal) => goal.inProgress),
  }
}
